import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Award, FlaskConical, Globe, Sunrise, Utensils, Sparkles, ShieldCheck, Archive, Plus, Minus } from "lucide-react";
import { MANUKA_BENEFITS } from "@/data/content";

const ICONS = { Award, FlaskConical, Globe, Sunrise, Utensils, Sparkles, ShieldCheck, Archive };

function BenefitCard({ b, open, onToggle }) {
  const Icon = ICONS[b.icon] || Award;
  return (
    <div className="rounded-2xl border border-wood-900/10 bg-cream transition-shadow hover:shadow-[0_8px_30px_rgba(62,49,40,0.08)]">
      <button
        onClick={onToggle}
        aria-expanded={open}
        data-testid={`manuka-benefit-${b.icon}`}
        className="flex w-full items-start gap-4 p-6 text-left"
      >
        <span className="flex h-12 w-12 flex-none items-center justify-center rounded-full bg-honey/15 text-honey-dark">
          <Icon size={22} />
        </span>
        <span className="flex-1">
          <span className="flex items-center justify-between gap-3">
            <span className="font-serif text-xl text-wood-900">{b.title}</span>
            <span className="flex-none text-honey-dark">{open ? <Minus size={18} /> : <Plus size={18} />}</span>
          </span>
          <span className="mt-1 block text-base leading-relaxed text-wood-700">{b.short}</span>
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <p className="px-6 pb-6 pl-[5.5rem] text-base leading-relaxed text-wood-700">{b.detail}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function ManukaEducation() {
  const [open, setOpen] = useState(null);
  return (
    <section className="mt-20 border-t border-wood-900/10 pt-16" data-testid="manuka-education" aria-labelledby="manuka-edu-heading">
      <div className="max-w-3xl">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-honey-dark">Manuka Honey MGO 234+ · Education</p>
        <h2 id="manuka-edu-heading" className="mt-3 font-serif text-4xl leading-tight text-wood-900 md:text-5xl">
          Understanding MGO 234+
        </h2>
        <div className="mt-6 rounded-2xl bg-sand p-6">
          <h3 className="font-serif text-2xl text-wood-900">What does MGO 234+ mean?</h3>
          <p className="mt-2 text-base leading-relaxed text-wood-700">
            MGO is a measured marker used to grade Manuka honey — it reflects the level of naturally occurring
            methylglyoxal. This product is rated <span className="font-semibold text-wood-900">MGO 234+</span>, a clear,
            comparable number rather than a vague promise.
          </p>
        </div>
      </div>

      <div className="mt-8 grid gap-4 md:grid-cols-2">
        {MANUKA_BENEFITS.map((b, i) => (
          <BenefitCard key={b.icon} b={b} open={open === i} onToggle={() => setOpen(open === i ? null : i)} />
        ))}
      </div>

      <p className="mt-8 max-w-3xl text-sm leading-relaxed text-wood-500">
        Manuka honey has been studied for its naturally occurring compounds and distinctive properties. Our products
        are foods and are not intended to diagnose, treat, cure, or prevent any disease.
      </p>
    </section>
  );
}
